// VISINVOKE — single-credential staff elevation page (DCC desktop sign-in bridge).
//
// Reached ONLY when /v1/desktop/authorize detects an already-authenticated account with ACTIVE staff
// membership that has not yet completed staff MFA on this web session. The account is ALREADY signed in
// (one credential entry, at DCC/customer login) — so this page shows ONLY the existing staff TOTP
// challenge (no email/password). It calls POST /v1/staff/login/mfa-session, which derives identity
// SERVER-SIDE from the authenticated session (never client-supplied) and reuses the exact staff MFA
// verification. On success it resumes the validated single-use DCC /authorize redirect. If MFA is not yet
// enrolled it routes to the existing /admin/mfa-setup flow; the persisted redirect resumes from the dashboard.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { captureDesktopRedirect, consumeDesktopRedirect } from './staffDesktopRedirect';

const STAFF_API = process.env.REACT_APP_API_URL || 'https://api.visinvoke.com';

export default function StaffMFAElevate() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { captureDesktopRedirect(); }, []);

  const resume = () => {
    const back = consumeDesktopRedirect();
    if (back) { window.location.assign(back); return; }
    navigate('/admin/dashboard', { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const res = await fetch(`${STAFF_API}/v1/staff/login/mfa-session`, {
        method: 'POST',
        credentials: 'include',                     // authenticated web session (identity is server-derived)
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ totp_code: code.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.status === 'authenticated') { resume(); return; }
      if (res.ok && data.status === 'mfa_setup_required') { navigate('/admin/mfa-setup', { replace: true }); return; }
      if (res.status === 403) { setError('This account does not have active staff access.'); return; }
      if (res.status === 429) { setError('Too many attempts. Please wait and try again.'); return; }
      setError(data.error || 'Invalid authenticator code.');
    } catch {
      setError('Verification service unavailable. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div style={st.wrap}>
      <form style={st.card} onSubmit={submit}>
        <div style={st.logo}>VISINVOKE</div>
        <h1 style={st.title}>Staff verification</h1>
        <p style={st.sub}>You're signed in. Enter your 6-digit authenticator code to continue to the Service Command Centre.</p>
        <input
          style={st.input}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={8}
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
          autoFocus
          aria-label="Authenticator code"
        />
        {error ? <div style={st.error}>{error}</div> : null}
        <button style={{ ...st.btn, opacity: busy || code.length < 6 ? 0.6 : 1 }} type="submit" disabled={busy || code.length < 6}>
          {busy ? 'Verifying…' : 'Verify'}
        </button>
        <div style={st.hint}>Staff sessions are monitored and logged. MFA required.</div>
      </form>
    </div>
  );
}

const st = {
  wrap: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0d1117', padding: 24 },
  card: { width: '100%', maxWidth: 400, background: '#161b22', border: '1px solid #30363d', borderRadius: 14, padding: '32px 28px', textAlign: 'center', boxShadow: '0 24px 70px rgba(0,0,0,0.5)' },
  logo: { fontWeight: 900, fontSize: 20, color: '#8b5cf6', letterSpacing: 0.5, marginBottom: 14 },
  title: { margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: '#e6edf3' },
  sub: { margin: '0 0 20px', fontSize: 14, color: '#8b949e', lineHeight: 1.6 },
  input: { width: '100%', boxSizing: 'border-box', padding: '14px 16px', fontSize: 22, letterSpacing: 6, textAlign: 'center', borderRadius: 10, border: '1px solid #30363d', background: '#0d1117', color: '#e6edf3', outline: 'none' },
  error: { marginTop: 12, color: '#f85149', fontSize: 13.5 },
  btn: { marginTop: 18, width: '100%', padding: '13px 0', border: 0, borderRadius: 10, cursor: 'pointer', color: '#fff', fontWeight: 800, fontSize: 15, background: 'linear-gradient(135deg,#6f3fe0,#8a4dff)' },
  hint: { marginTop: 16, color: 'rgba(255,255,255,0.3)', fontSize: 12 },
};
